// 대시보드 위젯이 쓰는 메트릭 계산 모음.
// 모두 클라이언트 사이드 계산 (09 §6.1 — 188건~1000건 규모).
//
// 보류 항목의 default 값은 아래 상수로 노출. 사용자 피드백 받으며 조정.

import type { Appraisal, DepositRow } from 'firebase/appraisals-service';

// ─── 보류 항목 default ─────────────────────────────────────────────────
export const TOP_N_UNPAID = 5; // 미수금 Top 표시 건수
export const STAGNANT_DAYS = 90; // 같은 status 에 N일 이상 머물면 "정체"
export const UPCOMING_WINDOW_DAYS = 14; // 다가오는 마감 — 향후 N일
export const TREND_MONTHS = 6; // 월별 차트 표시 개월 수
export const MIN_CONVERSION_SAMPLE = 5; // 전환률 표시 최소 표본. 미만이면 "표본 부족"
export const CATEGORY_TOP_N = 7; // 감정구분 도넛 — Top N 외엔 "기타"로 합산

// ─── 공용 유틸 ─────────────────────────────────────────────────────────
const toDate = (s?: string): Date | null => {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

const monthKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);

// ─── Row 1 · KPI 4개 ───────────────────────────────────────────────────
export interface KpiSummary {
  inProgressCount: number; // is_assigned=true & status≠완료
  thisMonthAssigned: number; // 이번 달 asign_date
  thisMonthAssignedDelta: number; // 전월 대비 %
  totalUnpaid: number; // sum(total_remainders where >0)
  expectedFeeWaiting: number; // is_assigned=false
}

export function computeKpi(rows: Appraisal[], now: Date = new Date()): KpiSummary {
  const thisMonth = monthKey(now);
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonth = monthKey(prev);

  let inProgressCount = 0;
  let thisMonthAssigned = 0;
  let prevMonthAssigned = 0;
  let totalUnpaid = 0;
  let expectedFeeWaiting = 0;

  for (const r of rows) {
    // is_assigned default true — 옛 188건 마이그레이션 정책 (09 §5.1)
    const assigned = r.is_assigned ?? true;
    if (assigned && r.status !== '완료') inProgressCount += 1;
    if (!assigned) expectedFeeWaiting += 1;

    const adate = toDate(r.asign_date);
    if (adate) {
      const k = monthKey(adate);
      if (k === thisMonth) thisMonthAssigned += 1;
      else if (k === prevMonth) prevMonthAssigned += 1;
    }

    if (r.total_remainders && r.total_remainders > 0) totalUnpaid += r.total_remainders;
  }

  const delta =
    prevMonthAssigned === 0
      ? thisMonthAssigned > 0
        ? 100
        : 0
      : ((thisMonthAssigned - prevMonthAssigned) / prevMonthAssigned) * 100;

  return {
    inProgressCount,
    thisMonthAssigned,
    thisMonthAssignedDelta: delta,
    totalUnpaid,
    expectedFeeWaiting
  };
}

// ─── Row 2 · 월별 청구 vs 입금 ──────────────────────────────────────────
export interface MonthlyBilling {
  month: string; // 'YYYY-MM'
  label: string; // 'M월'
  billed: number;
  paid: number;
}

export function computeMonthlyBilling(
  rows: Appraisal[],
  months: number = TREND_MONTHS,
  now: Date = new Date()
): MonthlyBilling[] {
  // 최근 N개월 버킷 초기화
  const buckets = new Map<string, MonthlyBilling>();
  for (let i = months - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const k = monthKey(d);
    buckets.set(k, { month: k, label: `${d.getMonth() + 1}월`, billed: 0, paid: 0 });
  }
  const cutoff = startOfMonth(new Date(now.getFullYear(), now.getMonth() - (months - 1), 1));

  const addToBucket = (
    when: Date,
    amount: number,
    field: 'billed' | 'paid'
  ) => {
    if (when < cutoff) return;
    const k = monthKey(when);
    const b = buckets.get(k);
    if (b) b[field] += amount;
  };

  for (const r of rows) {
    // 청구 = plaintiff_fee + defendant_fee 의 date 기준
    for (const f of r.plaintiff_fee ?? []) {
      const d = toDate(f.date);
      if (d) addToBucket(d, Number(f.amount) || 0, 'billed');
    }
    for (const f of r.defendant_fee ?? []) {
      const d = toDate(f.date);
      if (d) addToBucket(d, Number(f.amount) || 0, 'billed');
    }
    // 입금 = plaintiff_deposits + defendant_deposits
    for (const f of r.plaintiff_deposits ?? []) {
      const d = toDate(f.date);
      if (d) addToBucket(d, Number(f.amount) || 0, 'paid');
    }
    for (const f of r.defendant_deposits ?? []) {
      const d = toDate(f.date);
      if (d) addToBucket(d, Number(f.amount) || 0, 'paid');
    }
  }

  return Array.from(buckets.values());
}

// ─── Row 2 · 미수금 Top N ──────────────────────────────────────────────
export interface UnpaidRow {
  id: string;
  title: string;
  court: string;
  caseNumber: string;
  unpaid: number;
  lastDepositDate: string; // 'YYYY-MM-DD' or ''
  neverPaid: boolean; // 단 한 번도 입금 없음
  isAssigned: boolean; // 사건 상세 라우트 분기용
}

export function computeTopUnpaid(rows: Appraisal[], n: number = TOP_N_UNPAID): UnpaidRow[] {
  const lastDeposit = (r: Appraisal): string => {
    const all: DepositRow[] = [...(r.plaintiff_deposits ?? []), ...(r.defendant_deposits ?? [])];
    const dates = all.map((d) => d.date).filter((s): s is string => !!s);
    if (dates.length === 0) return '';
    return dates.sort().slice(-1)[0]!;
  };

  return rows
    .filter((r) => (r.total_remainders ?? 0) > 0)
    .map<UnpaidRow>((r) => {
      const last = lastDeposit(r);
      return {
        id: r.id,
        title: r.title ?? '',
        court: r.court ?? '',
        caseNumber: r.case_fullnumber ?? '',
        unpaid: r.total_remainders ?? 0,
        lastDepositDate: last,
        neverPaid: last === '',
        isAssigned: r.is_assigned ?? true
      };
    })
    .sort((a, b) => b.unpaid - a.unpaid)
    .slice(0, n);
}

// ─── Row 3 · 의뢰상태 분포 ─────────────────────────────────────────────
export interface StatusDistRow {
  label: string;
  count: number;
}

const STATUS_ORDER = [
  '예상감정료 제출',
  '감정인 지정',
  '감정자료요청',
  '감정보고서 제출',
  '사실조회 (1)',
  '사실조회 (2)',
  '완료'
];

export function computeStatusDistribution(rows: Appraisal[]): StatusDistRow[] {
  const map = new Map<string, number>();
  for (const r of rows) {
    if (!(r.is_assigned ?? true)) continue; // 예상감정료 단계는 제외 — 진행 사건만
    if (r.status === '완료') continue; // KPI inProgressCount 와 일관
    const s = r.status ?? '(미지정)';
    map.set(s, (map.get(s) ?? 0) + 1);
  }
  // master 정의 순서 우선, 그 외는 뒤에
  const result: StatusDistRow[] = [];
  for (const label of STATUS_ORDER) {
    const c = map.get(label) ?? 0;
    if (c > 0) result.push({ label, count: c });
    map.delete(label);
  }
  for (const [label, count] of map) result.push({ label, count });
  return result;
}

// ─── Row 3 · 수임 전환률 ───────────────────────────────────────────────
// 이번 달 신규 등록 사건 중 is_assigned=true 비율.
// 등록은 create_timestamp 기준, fallback 으로 asign_date 의 월.
export interface ConversionStat {
  rate: number; // 0~100
  assignedCount: number;
  totalCount: number;
}

export function computeConversionRate(rows: Appraisal[], now: Date = new Date()): ConversionStat {
  const thisMonth = monthKey(now);
  let total = 0;
  let assigned = 0;
  for (const r of rows) {
    const ts = r.create_timestamp?.toDate?.() ?? toDate(r.asign_date);
    if (!ts) continue;
    if (monthKey(ts) !== thisMonth) continue;
    total += 1;
    if (r.is_assigned ?? true) assigned += 1;
  }
  return {
    rate: total === 0 ? 0 : (assigned / total) * 100,
    assignedCount: assigned,
    totalCount: total
  };
}

// ─── Row 3 · 정체 사건 ─────────────────────────────────────────────────
// modify_timestamp 가 N일 이상 안 바뀐 진행 중 사건.
// (status 별 체류 시간을 정확히 알려면 history 가 필요한데 없으므로 modify 로 근사)
export interface StagnantRow {
  id: string;
  title: string;
  status: string;
  stagnantDays: number;
  caseNumber: string;
  isAssigned: boolean;
}

export function computeStagnant(
  rows: Appraisal[],
  thresholdDays: number = STAGNANT_DAYS,
  now: Date = new Date()
): StagnantRow[] {
  const result: StagnantRow[] = [];
  for (const r of rows) {
    if (!(r.is_assigned ?? true)) continue;
    if (r.status === '완료') continue;
    const ts = r.modify_timestamp?.toDate?.();
    if (!ts) continue;
    const days = Math.floor((now.getTime() - ts.getTime()) / (1000 * 60 * 60 * 24));
    if (days >= thresholdDays) {
      result.push({
        id: r.id,
        title: r.title ?? '',
        status: r.status ?? '(미지정)',
        stagnantDays: days,
        caseNumber: r.case_fullnumber ?? '',
        isAssigned: r.is_assigned ?? true
      });
    }
  }
  return result.sort((a, b) => b.stagnantDays - a.stagnantDays);
}

// ─── Row 4 · 다가오는 마감 ─────────────────────────────────────────────
// appraisal_progress[].end_date 가 향후 N일 이내 (오늘 포함, 과거 제외).
export interface UpcomingRow {
  id: string;
  title: string;
  stageName: string; // 진행 단계명 (자료요청/현장조사/...)
  dueDate: string;
  dDay: number; // 음수면 지남, 0=오늘
  isAssigned: boolean;
}

export function computeUpcoming(
  rows: Appraisal[],
  windowDays: number = UPCOMING_WINDOW_DAYS,
  now: Date = new Date()
): UpcomingRow[] {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const result: UpcomingRow[] = [];
  for (const r of rows) {
    for (const p of r.appraisal_progress ?? []) {
      const d = toDate(p.end_date);
      if (!d) continue;
      const dueDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const diff = Math.round((dueDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (diff < 0 || diff > windowDays) continue;
      result.push({
        id: r.id,
        title: r.title ?? '',
        stageName: p.type ?? '',
        dueDate: p.end_date ?? '',
        dDay: diff,
        isAssigned: r.is_assigned ?? true
      });
    }
  }
  return result.sort((a, b) => a.dDay - b.dDay);
}

// ─── Row 4 · 감정 구분별 분포 (최근 12개월) ────────────────────────────
export interface CategoryDistRow {
  label: string;
  count: number;
}

export function computeCategoryDistribution(rows: Appraisal[], now: Date = new Date()): CategoryDistRow[] {
  const cutoff = new Date(now.getFullYear() - 1, now.getMonth(), 1);
  const map = new Map<string, number>();
  for (const r of rows) {
    const ts = r.create_timestamp?.toDate?.() ?? toDate(r.asign_date);
    if (!ts || ts < cutoff) continue;
    const cat = r.appraisal_category ?? '(미지정)';
    map.set(cat, (map.get(cat) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}
